/**
 * Hey Clay mega menus (Shop, Universes) on top of the Impulse header.
 * Port of `apps/main/app/composables/useHeaderDropdown.ts` and `useHeaderMenuAnalytics.ts` from the Nuxt site.
 *
 * Only `details.hc-megamenu` items are handled here (they're rendered with data-hover="false", so Impulse's own
 * instant hover menu skips them); every other menu item keeps Impulse's behaviour.
 *
 * Desktop
 * - Mouse: opens on hover intent (https://baymard.com/research-articles/dropdown-menu-flickering-issue): once the
 *   pointer rests on the item, not while it passes over on its way elsewhere. Closes after a short grace period, so
 *   the pointer can travel to the panel or slip out of it briefly. A click on the item navigates to its link.
 * - Touch: a tap toggles the panel instead of navigating.
 * - Keyboard: like Impulse's menus, focusing the item opens it and Enter follows its link; Tab moves through the
 *   panel, Escape closes it and focus leaving it closes it.
 *
 * Mobile (hc-mobile-menu.liquid, opened by Impulse's hamburger): a full-screen dialog like the Nuxt site's, with a
 * root screen and one screen per mega menu / submenu, region and language. Focus moves in on open and back on close,
 * Tab stays inside, Escape closes, the page behind doesn't scroll. Shop sections are an animated accordion.
 *
 * Analytics: the same events and fields as the Nuxt site, pushed to `dataLayer` and published as Shopify customer
 * events (`hey_clay:<event>`), so both can be reported together.
 * - header_nav_click   { nav_item, menu_variant }            top-level navigation links
 * - header_menu_open   { menu, menu_variant }                once per menu and variant per page
 * - header_menu_click  { menu, menu_variant, menu_section, item_id, item_name, item_position, link_url }
 * - header_menu_expand { menu, menu_variant, menu_section, expand_type }   "See more" / mobile section opened
 * - filter_link_click  { filter_type, filter_value, filter_source: 'header_menu' }   age and size tiles
 */
(function () {
  'use strict';

  var INTENT_INTERVAL = 100;
  var INTENT_SENSITIVITY = 6;
  var CLOSE_DELAY = 300;
  // Matches the close animation in hc-mega-menu.css
  var CLOSE_ANIMATION = 200;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------------------------------- analytics */

  function track(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, payload));
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
      window.Shopify.analytics.publish('hey_clay:' + event, payload);
    }
  }

  function variantOf(el) {
    return el.closest('#HcMobileMenu') ? 'mobile' : 'desktop';
  }

  var loggedOpens = {};
  function logMenuOpen(menu, variant) {
    var key = menu + ':' + variant;
    if (loggedOpens[key]) return;
    loggedOpens[key] = true;
    track('header_menu_open', { menu: menu, menu_variant: variant });
  }

  function logNavClick(el) {
    track('header_nav_click', { nav_item: el.dataset.hcNavItem, menu_variant: variantOf(el) });
  }

  /* -------------------------------------------------------------------------------- desktop */

  var menus = Array.prototype.slice.call(document.querySelectorAll('details.hc-megamenu'));

  // Closing plays the panel's close animation first; opening (again) cancels a close in progress
  function setOpen(details, open) {
    clearTimeout(details.hcCloseAnimation);
    if (open) {
      details.classList.remove('is-closing');
      if (!details.open) details.open = true;
      return;
    }
    if (!details.open || details.classList.contains('is-closing')) return;
    if (reducedMotion.matches) {
      details.open = false;
      return;
    }
    details.classList.add('is-closing');
    details.hcCloseAnimation = setTimeout(function () {
      details.classList.remove('is-closing');
      details.open = false;
    }, CLOSE_ANIMATION);
  }

  menus.forEach(function (details) {
    var summary = details.querySelector('summary');
    var menu = details.dataset.hcMenu;
    var intentInterval;
    var closeTimeout;
    var pointerType = 'mouse';
    var pointer = { x: 0, y: 0 };

    function cancelTimers() {
      clearInterval(intentInterval);
      clearTimeout(closeTimeout);
    }

    details.addEventListener('toggle', function () {
      summary.setAttribute('aria-expanded', String(details.open));
      // Closed directly (Impulse's click outside, another panel opening): no animation left to finish
      if (!details.open) {
        clearTimeout(details.hcCloseAnimation);
        details.classList.remove('is-closing');
        return;
      }
      cancelTimers();
      // One panel at a time, Impulse's own menus included
      document.querySelectorAll('.site-nav__details[open]').forEach(function (other) {
        if (other !== details) other.removeAttribute('open');
      });
      logMenuOpen(menu, 'desktop');
    });

    details.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      cancelTimers();
      if (details.open) {
        // Back while it's animating closed: keep it
        if (details.classList.contains('is-closing')) setOpen(details, true);
        return;
      }
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      var last = { x: pointer.x, y: pointer.y };
      intentInterval = setInterval(function () {
        if (Math.hypot(pointer.x - last.x, pointer.y - last.y) < INTENT_SENSITIVITY) {
          clearInterval(intentInterval);
          setOpen(details, true);
        } else {
          last = { x: pointer.x, y: pointer.y };
        }
      }, INTENT_INTERVAL);
    });

    details.addEventListener('pointermove', function (e) {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
    });

    details.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      cancelTimers();
      if (details.open) closeTimeout = setTimeout(function () { setOpen(details, false); }, CLOSE_DELAY);
    });

    summary.addEventListener('pointerdown', function (e) {
      pointerType = e.pointerType;
    });

    // A mouse click or Enter follows the link; a tap falls through to the native details toggle
    summary.addEventListener('click', function (e) {
      var link = summary.dataset.link;
      var isTap = pointerType !== 'mouse' && e.detail !== 0;
      pointerType = 'mouse';
      if (isTap || !link || link === '#') return;
      e.preventDefault();
      logNavClick(summary);
      window.location.href = link;
    });

    // Keyboard focus only (`:focus-visible`): a mouse click focuses the summary too, and a tap must toggle once
    summary.addEventListener('focus', function () {
      if (details.dataset.hcRefocus) {
        delete details.dataset.hcRefocus;
        return;
      }
      if (summary.matches(':focus-visible')) setOpen(details, true);
    });

    // `relatedTarget` is null when clicking a non-focusable part of the panel, which must not close it
    details.addEventListener('focusout', function (e) {
      if (details.open && e.relatedTarget && !details.contains(e.relatedTarget)) setOpen(details, false);
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    menus.forEach(function (details) {
      if (!details.open) return;
      var focusInside = details.contains(document.activeElement);
      setOpen(details, false);
      var summary = details.querySelector('summary');
      if (!focusInside || document.activeElement === summary) return;
      // Back on the item without reopening it
      details.dataset.hcRefocus = 'true';
      summary.focus();
    });
  });

  /* -------------------------------------------------------------------------------- see more */

  function toggleSeeMore(button) {
    var column = button.parentElement;
    var expanded = !column.classList.contains('is-expanded');
    column.classList.toggle('is-expanded', expanded);
    button.setAttribute('aria-expanded', String(expanded));
    button.querySelector('span').textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    if (expanded) {
      track('header_menu_expand', {
        menu: button.dataset.hcMenu,
        menu_variant: variantOf(button),
        menu_section: button.dataset.hcSection,
        expand_type: 'see_more',
      });
    }
  }

  /* -------------------------------------------------------------------------------- accordion */

  // Mobile Shop sections: height animated from 0 to the content's and back, `hidden` when closed
  function toggleAccordion(button) {
    var content = document.getElementById(button.getAttribute('aria-controls'));
    if (!content) return;
    var open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    clearTimeout(content.hcAccordionTimeout);

    if (reducedMotion.matches) {
      content.hidden = !open;
      content.style.height = '';
    } else if (open) {
      content.hidden = false;
      content.style.height = '0px';
      void content.offsetHeight; // reflow, so the transition starts from the height just set
      content.style.height = content.scrollHeight + 'px';
      // `auto` afterwards, so "See more" inside can still grow it
      content.hcAccordionTimeout = setTimeout(function () { content.style.height = ''; }, CLOSE_ANIMATION);
    } else {
      content.style.height = content.scrollHeight + 'px';
      void content.offsetHeight;
      content.style.height = '0px';
      content.hcAccordionTimeout = setTimeout(function () {
        content.hidden = true;
        content.style.height = '';
      }, CLOSE_ANIMATION);
    }

    if (open) {
      track('header_menu_expand', {
        menu: 'shop',
        menu_variant: variantOf(button),
        menu_section: button.dataset.hcExpandSection,
        expand_type: 'section',
      });
    }
  }

  /* -------------------------------------------------------------------------------- mobile menu */

  var mobileMenu = document.getElementById('HcMobileMenu');
  if (mobileMenu) initMobileMenu(mobileMenu);

  function initMobileMenu(dialog) {
    var openers = Array.prototype.slice.call(document.querySelectorAll('.js-drawer-open-nav'));
    var content = dialog.querySelector('[data-hc-mobile-content]');
    var backButton = dialog.querySelector('[data-hc-mobile-back]');
    var closeButton = dialog.querySelector('[data-hc-mobile-close]');
    var title = dialog.querySelector('[data-hc-mobile-title]');
    var rootOnly = dialog.querySelectorAll('[data-hc-root-only]');
    var focusBeforeOpen = null;
    var closeTimeout;

    openers.forEach(function (button) {
      button.setAttribute('aria-controls', dialog.id);
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', 'false');
      // Capture phase: before anything else bound to the hamburger
      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        open();
      }, true);
    });

    function showScreen(name) {
      var screen = dialog.querySelector('[data-hc-screen="' + name + '"]');
      if (!screen) return;
      dialog.querySelectorAll('[data-hc-screen]').forEach(function (s) { s.hidden = s !== screen; });
      var isRoot = name === 'root';
      backButton.hidden = isRoot;
      title.hidden = isRoot;
      title.textContent = isRoot ? '' : screen.dataset.title || '';
      rootOnly.forEach(function (el) { el.hidden = !isRoot; });
      content.scrollTop = 0;
    }

    function setOpeners(expanded) {
      openers.forEach(function (button) { button.setAttribute('aria-expanded', String(expanded)); });
    }

    function open() {
      if (!dialog.hidden && !dialog.classList.contains('is-closing')) return;
      clearTimeout(closeTimeout);
      dialog.classList.remove('is-closing');
      focusBeforeOpen = document.activeElement;
      showScreen('root');
      dialog.hidden = false;
      if (!reducedMotion.matches) {
        dialog.classList.add('is-opening');
        setTimeout(function () { dialog.classList.remove('is-opening'); }, CLOSE_ANIMATION);
      }
      document.documentElement.classList.add('hc-mobile-menu-open');
      setOpeners(true);
      closeButton.focus();
    }

    function close() {
      if (dialog.hidden || dialog.classList.contains('is-closing')) return;
      document.documentElement.classList.remove('hc-mobile-menu-open');
      setOpeners(false);
      var finish = function () {
        dialog.classList.remove('is-closing');
        dialog.hidden = true;
        showScreen('root');
      };
      if (reducedMotion.matches) {
        finish();
      } else {
        dialog.classList.add('is-closing');
        closeTimeout = setTimeout(finish, CLOSE_ANIMATION);
      }
      if (focusBeforeOpen && focusBeforeOpen.focus) focusBeforeOpen.focus();
      focusBeforeOpen = null;
    }

    closeButton.addEventListener('click', close);
    backButton.addEventListener('click', function () { showScreen('root'); });

    dialog.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-hc-open-screen]');
      if (opener) {
        showScreen(opener.dataset.hcOpenScreen);
        if (opener.dataset.hcMenuScreen) logMenuOpen(opener.dataset.hcMenuScreen, 'mobile');
        return;
      }
      // A link opening a page closes the menu (the page may be the current one, e.g. a filter change)
      if (e.target.closest('a[href]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (dialog.hidden || dialog.classList.contains('is-closing')) return;
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      // `aria-modal`: Tab wraps around inside the dialog
      var focusable = Array.prototype.filter.call(
        dialog.querySelectorAll('a[href], button:not([disabled])'),
        function (el) { return el.offsetParent !== null; }
      );
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Impulse's header switches to the desktop nav from 769px: don't leave the menu open behind it
    window.matchMedia('(min-width: 769px)').addEventListener('change', function (e) {
      if (e.matches) close();
    });
  }

  /* -------------------------------------------------------------------------------- clicks */

  document.addEventListener('click', function (e) {
    var target = e.target.closest('[data-hc-item], [data-hc-see-more], [data-hc-accordion], [data-hc-nav-item]');
    if (!target) return;

    if (target.hasAttribute('data-hc-see-more')) {
      toggleSeeMore(target);
      return;
    }

    if (target.hasAttribute('data-hc-accordion')) {
      toggleAccordion(target);
      return;
    }

    if (target.hasAttribute('data-hc-item')) {
      var data = target.dataset;
      if (data.hcFilterType) {
        track('filter_link_click', {
          filter_type: data.hcFilterType,
          filter_value: data.hcFilterValue,
          filter_source: 'header_menu',
        });
      }
      track('header_menu_click', {
        menu: data.hcMenu,
        menu_variant: variantOf(target),
        menu_section: data.hcSection,
        item_id: data.hcItemId,
        item_name: data.hcItemName,
        item_position: Number(data.hcPosition),
        link_url: target.getAttribute('href'),
      });
      return;
    }

    // Our desktop summaries log their own navigation (a tap on them only toggles)
    if (target.closest('details.hc-megamenu')) return;
    logNavClick(target);
  });
})();
